import { useState, useEffect } from 'react';
import styled from 'styled-components';
import ArrowDownIcon from '@/assets/pc/Arrow-Down.svg?react';
import { Dropdown, MenuProps, Spin } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSearchLoadingStore } from '@/store/useSearchLoadingStore';

export default function SearchForm() {
  const location = useLocation();
  const [searchType, setSearchType] = useState<'전체' | '제목' | '내용'>('전체');
  const [input, setInput] = useState('');
  const navigate = useNavigate();
  const { loading, setLoading } = useSearchLoadingStore();

  useEffect(() => {
    setLoading(false); // 페이지 이동 감지 시 로딩 종료
  }, [location.pathname, location.search]);

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    setSearchType(e.key as '전체' | '제목' | '내용');
  };

  const handleSearch = () => {
    const newParams = new URLSearchParams();
    newParams.set('query', input);
    newParams.set('searchType', searchType);

    navigate({
      pathname: '/review',
      search: newParams.toString(),
    });
  };

  const menuItems: MenuProps['items'] = [
    { key: '전체', label: '전체' },
    { key: '제목', label: '제목' },
    { key: '내용', label: '내용' },
  ];

  return (
    <>
      {loading && (
        <FullScreenLoader>
          <Spin size="large" />
        </FullScreenLoader>
      )}
      <Container>
        <TitleGroup>
          <Title>후기</Title>
          <Subtitle>나와 비슷한 문제에 대한 후기를 찾아보세요</Subtitle>
        </TitleGroup>

        <ControlGroup>
          <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={['click']}>
            <DropdownBox>
              <DropdownContent>
                <DropdownText>{searchType}</DropdownText>
                <ArrowDownIcon />
              </DropdownContent>
            </DropdownBox>
          </Dropdown>

          <SearchInputBox $active={!!input}>
            <InputWrapper>
              <SearchInput
                placeholder="검색어를 입력하세요"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && input.trim()) {
                    handleSearch();
                  }
                }}
              />
            </InputWrapper>
          </SearchInputBox>
          <SearchButton disabled={!input} onClick={handleSearch}>
            {'검색'}
          </SearchButton>
        </ControlGroup>
      </Container>
    </>
  );
}

// Styled Components ----------------

const FullScreenLoader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  max-width: 1230px;
  padding: 80px 16px 45px 0;
  justify-content: space-between;
  align-items: flex-end;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h2`
  color: var(--gray-ui_gray02, #252525);
  font-size: 32px;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.64px;
  text-align: start;
`;

const Subtitle = styled.p`
  color: var(--gray-ui_gray02, #252525);
  font-size: 18px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.36px;
  text-align: start;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DropdownBox = styled.div`
  display: flex;
  width: 121px;
  height: 48px;
  padding: 0 12px;
  align-items: center;
  border-radius: 8px;
  border: 1px solid var(--table-line-table_line01, #bdbdbd);
  background: var(--Primary-white, #fff);
  cursor: pointer;
`;

const DropdownContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const DropdownText = styled.span`
  color: var(--gray-ui_gray04, #6f6f6f);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.32px;
`;

const SearchInputBox = styled.div<{ $active: boolean }>`
  display: flex;
  width: 221px;
  height: 48px;
  padding: 4px 10px;
  align-items: center;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? '#000C24' : '#bdbdbd')};
  background: var(--Primary-white, #fff);
`;

const InputWrapper = styled.div`
  display: flex;
  padding: 0 2px;
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 16px;
`;

const SearchButton = styled.button`
  min-width: 86px;
  height: 48px;
  padding: 0 12px;
  border-radius: 8px;
  border: none;
  cursor: pointer;

  background: ${({ disabled }) => (disabled ? '#f3f3f3' : '#0B46DD')};
  color: ${({ disabled }) => (disabled ? '#6f6f6f' : '#fff')};
  font-family: 'Spoqa Han Sans Neo';
  font-size: 16px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.32px;
`;
